import { SemanticSearchMode } from '@upright/ui/api/model/search/types';
import { uppercaseFirstLetter } from '@upright/ui/untidy/texts';
import { KeyboardEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select, { GroupBase, Props as SelectProps, StylesConfig, FilterOptionOption } from 'react-select';
import { ProductContext } from '../../../../../../common/model/input/companies/api-types';
import { ModelEntityType } from '../../../../../../common/model/input/types';
import { computeMatchQuality } from '../../../../../../common/model/search/match-quality';
import { ModelEntitySearchResponseItem, SearchEntityType } from '../../../../../../common/model/search/types';
import { loggedInUser } from '../../../../../untidy/redux/selectors';
import { useAutoFocus } from '../../../../generic/contexts/autofocus-context';
import { CustomControl } from './components/custom-control';
import { CustomInput } from './components/custom-input';
import { getCustomOption } from './components/custom-option';
import { CustomPlaceholder } from './components/custom-placeholder';
import { CustomMenuList, getMatchConfidenceMenuList } from './components/menu-list';
import { getCustomSingleValue } from './components/single-value';
import { searchStyles } from './styles';
import { useEntitySearch } from './use-entity-search';
import { useTrackableSearchInputs } from './use-trackable-inputs';
import { CustomValueContainer } from './value-container';

/**
 * Important: semantic search modes 'only semantic' and 'combine both' can only be used with product-like searchTypes.
 * Setting semantic search on with other searchTypes throws an error. -ML
 */

type Props = {
  // Note: The order of preference in array matters if both .COMPANY and 'legalEntity' are found,
  // since they are deduped but the search result object differs slightly.
  searchTypes?: readonly SearchEntityType[] | 'all';
  semanticSearchMode?: SemanticSearchMode;
  onChange: (value?: ModelEntitySearchResponseItem) => void;
  autoFocus?: boolean;
  selectedValue?: ModelEntitySearchResponseItem | null;
  filterOptions?: (option: FilterOptionOption<ModelEntitySearchResponseItem>, inputValue: string) => boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  styleOverride?: StylesConfig<ModelEntitySearchResponseItem>;
  isMulti?: boolean;
  creatable?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>, query?: string) => void;
  productContext?: ProductContext;
  defaultOptions?: ModelEntitySearchResponseItem[];
  placeholder?: string;
  disabled?: boolean;
  disableTracking?: boolean;
  allowSingleChar?: true;
  customInput?: any;
  disableButtonClicks?: boolean;
  enableAllResults?: boolean;
  maxResultsToShow?: number;
  includeVisibility?: boolean;
  showProductCode?: boolean;
  highlightUnMappableProduct?: boolean;
  shouldHideSpecialInformationIfNotEnoughSpace?: boolean;
  // Opt-in match-confidence checking for company search (self-serve portfolio
  // picker). When set, company rows show disambiguating country / listing-status
  // metadata and a "no confident match" banner appears when the top hit fails
  // confidence gating. Off (undefined) everywhere else, so no other search box
  // changes. See PLT-5578.
  showMatchConfidence?: boolean;
  id?: string;
} & Pick<SelectProps<ModelEntitySearchResponseItem, false, GroupBase<ModelEntitySearchResponseItem>>, 'components'>;

export default function Search(props: Props) {
  const {
    selectedValue,
    searchTypes,
    filterOptions,
    onMenuOpen,
    onMenuClose,
    creatable,
    onKeyDown,
    onChange,
    productContext,
    placeholder,
    disabled,
    disableTracking,
    disableButtonClicks,
    enableAllResults,
    id,
    shouldHideSpecialInformationIfNotEnoughSpace,
  } = props;
  const user = useSelector(loggedInUser);

  const { input, setInput, sendAttempts } = useTrackableSearchInputs(disableTracking);
  const { ref } = useAutoFocus<ModelEntitySearchResponseItem, false>();

  const entitySearch = useEntitySearch(input, searchTypes ?? 'all', {
    semanticSearchMode: props.semanticSearchMode,
    skipShortQueryFilter: props.allowSingleChar,
    includeVisibility: props.includeVisibility,
    allResultsEnabled: props.enableAllResults,
    maxResultsToShow: props.maxResultsToShow,
    showProductCode: props.showProductCode,
  });

  // If "creatable" prop is set, add the creatable option to be the first. This only applies to products for now.
  const optionsToDisplay: ModelEntitySearchResponseItem[] = (() => {
    if (!entitySearch.preprocessedSearchInput) {
      return props.defaultOptions ?? [];
    }
    if (creatable) {
      const creatableOption = {
        id: '',
        name: `${uppercaseFirstLetter(input)}`,
        type: ModelEntityType.PRODUCT,
        created: true,
        isDisabled: false,
        query: entitySearch.preprocessedSearchInput,
      };
      return [creatableOption, ...entitySearch.searchOptions];
    }
    return entitySearch.searchOptions;
  })();

  useEffect(() => {
    if (productContext) {
      // Initialize with highlighted value when searching with mapper plugin
      const phrase = productContext.phrase || '';
      setInput(phrase);
    }
  }, [productContext, ref, setInput]);

  // Match-confidence gating (opt-in via showMatchConfidence). Deterministic and
  // cheap — runs over the already-fetched company hits, no extra request. The
  // ES hits arrive ranked, but sort defensively so items[0] is the top score
  // computeMatchQuality expects.
  const matchQuality = (() => {
    if (!props.showMatchConfidence || !input) return undefined;
    const companyOptions = optionsToDisplay
      .filter(option => option.type === ModelEntityType.COMPANY)
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    return computeMatchQuality(companyOptions, { query: input });
  })();

  return (
    <Select
      id={id}
      ref={ref}
      value={selectedValue ? selectedValue : null}
      onChange={(value: ModelEntitySearchResponseItem | null) => {
        if (value) {
          sendAttempts({
            entityRef: {
              // TODO ML: Ask William if this should be removed
              id: value.id,
              type: value.type,
            },
          });
        }
        onChange(value ?? undefined);
      }}
      inputValue={input}
      onInputChange={(value?: string) => {
        setInput(value || '');
      }}
      onKeyDown={
        onKeyDown &&
        ((e: KeyboardEvent<HTMLDivElement>) => {
          onKeyDown(e, input);
        })
      }
      options={optionsToDisplay}
      isLoading={
        !!entitySearch.preprocessedSearchInput &&
        (entitySearch.basicQuery.isLoading || entitySearch.semanticQuery.isLoading || entitySearch.isDebouncing)
      }
      loadingMessage={() => (entitySearch.errorMessage ? entitySearch.errorMessage : 'Searching…')}
      openMenuOnFocus={!!productContext?.phrase}
      components={{
        Option: getCustomOption({
          headerSearch: false,
          shouldHideSpecialInformationIfNotEnoughSpace,
          theme: 'light',
          user,
          creatable,
          productContext,
          disableButtonClicks,
          enableAllResults,
          showDisambiguationMetadata: props.showMatchConfidence,
        }),
        IndicatorSeparator: EmptyComponent,
        DropdownIndicator: EmptyComponent,
        MenuList: props.showMatchConfidence ? getMatchConfidenceMenuList(matchQuality) : CustomMenuList,
        SingleValue: getCustomSingleValue(props.showProductCode, props.highlightUnMappableProduct),
        ValueContainer: CustomValueContainer,
        LoadingIndicator: EmptyComponent,
        Input: CustomInput,
        Placeholder: CustomPlaceholder,
        Control: CustomControl,
        ...props.components,
      }}
      isClearable={true}
      isDisabled={disabled}
      styles={{ ...searchStyles, ...props.styleOverride }}
      placeholder={placeholder || getPlaceholder(searchTypes)}
      isMulti={false}
      filterOption={filterOptions ?? null}
      noOptionsMessage={inputValue => {
        if (selectedValue) {
          return 'Type to search for a new value';
        }
        if (entitySearch.errorMessage) {
          return entitySearch.errorMessage;
        }
        return inputValue.inputValue.length > 2
          ? `No results matching "${inputValue.inputValue}"`
          : 'Type in a few more letters...';
      }}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      onBlur={() => {
        sendAttempts();
      }}
    />
  );
}

const EmptyComponent = () => <div />;

function grammarizedSearchTypeText(searchType: SearchEntityType) {
  switch (searchType) {
    case 'legalEntity':
      return 'legal entities';
    case ModelEntityType.COMPANY:
      return 'companies';
    case ModelEntityType.PRODUCT:
      return 'products or services';
    case ModelEntityType.MIXIN:
      return 'mixins';
    case ModelEntityType.PORTFOLIO:
      return 'portfolios';
    case ModelEntityType.GROUP:
      return 'company groups';
    case ModelEntityType.PHENOMENON:
      return 'phenomena';
  }
}

function getPlaceholder(searchTypes?: readonly SearchEntityType[] | 'all') {
  if (searchTypes === 'all' || !searchTypes || searchTypes.length === 0) {
    return 'Search for products, services, or companies…';
  }
  switch (searchTypes.length) {
    case 1:
      return `Search for ${grammarizedSearchTypeText(searchTypes[0])}…`;
    case 2:
      return `Search for ${searchTypes
        .slice(0, -1)
        .map(type => grammarizedSearchTypeText(type))
        .join(', ')} or ${grammarizedSearchTypeText(searchTypes.slice(-1)[0])}…`;
    default:
      return `Search for ${searchTypes
        .slice(0, -1)
        .map(type => ` ${grammarizedSearchTypeText(type)}`)
        .join(', ')}, or ${grammarizedSearchTypeText(searchTypes.slice(-1)[0])}…`;
  }
}
